/**
 * Corrections after work is entered: a saree's start date ("on the loom since") and number, a loom's
 * code. The server checks every change; these helpers only shape what the screens show.
 */

/** The server's answer to "would a start on this day work?" (GET /sarees/{id}/start-check). */
export type StartCheck = {
  currentStart?: string | null;
  proposedStart?: string | null;
  firstEntryDate?: string | null;
  earliestAllowed?: string | null;
  previousSareeNumber?: string | null;
  ok?: boolean;
  problem?: string | null;
};

/**
 * Bounds for the date box: never before the day after the loom's previous saree ended, never after
 * the first day with an entry, never in the future. Empty when there is no bound.
 */
export const startBounds = (check: StartCheck | null, today: string): { min: string; max: string } => {
  const first = check?.firstEntryDate || "";
  return {
    min: check?.earliestAllowed || "",
    max: first && first < today ? first : today,
  };
};

/** One-tap "Move its start to <day>?" offer for a loom with no saree on the day but one from a later day. */
export type MoveStartOffer = {
  pieceId?: string;
  sareeNumber: string;
  designNumber?: string;
  currentStart: string;
  proposedStart: string;
  allowed: boolean;
  problem?: string | null;
  canMove: boolean;
  /** Supervisor: an entry on this day waits in Approvals together with the start move. */
  needsApproval?: boolean;
};

export const moveStartOffer = (loom: any): MoveStartOffer | null => {
  const later = loom?.laterSaree;
  const m = later?.moveStart;
  if (!later || !m) return null;
  return {
    pieceId: later.pieceId,
    sareeNumber: String(later.sareeNumber ?? ""),
    designNumber: later.designNumber || undefined,
    currentStart: m.currentStart || later.startedOn || later.from,
    proposedStart: m.proposedStart,
    allowed: !!m.allowed,
    problem: m.problem || null,
    canMove: !!m.canMove,
    needsApproval: m.needsApproval ?? !m.canMove,
  };
};

/**
 * A day before a later saree's start that can be filled in like a running loom (Daily work rows, the
 * loom page): saving moves the start (owner/manager) or sends entry + move to Approvals (supervisor).
 * Also kept when a supervisor's entry for the day already waits in Approvals with the move.
 */
export const backfillOffer = (loom: any): MoveStartOffer | null => {
  if (!loom || loom.assignment || loom.state === "CLOSED") return null;
  const offer = moveStartOffer(loom);
  const waitingMove = (loom.entries || []).some((e: any) => e.pendingStart?.status === "PENDING");
  return offer && offer.pieceId && (offer.allowed || waitingMove) ? offer : null;
};

/**
 * The part a back-filled saree's entry starts on: `laterSaree.startWorkTypeId` (its first entry's part,
 * else its set-up part), else "" so the part is picked by hand (never guessed: the wrong part pays the wrong rate).
 */
export const backfillPart = (loom: any): string => loom?.laterSaree?.startWorkTypeId || "";

/** The request fields that make an entry also move (or ask to move) the later saree's start. */
export const backfillBody = (offer: MoveStartOffer | null | undefined) =>
  offer?.pieceId ? { moveStart: true, moveStartPieceId: offer.pieceId, moveStartFrom: offer.currentStart } : {};

/** A change-log row (saree or loom history): who, when, what, old → new. */
export type ChangeRow = {
  field: string;
  oldValue?: string | null;
  newValue?: string | null;
  reason?: string | null;
  source?: string | null;
  byName?: string | null;
  at?: string | number | null;
};

export const CHANGE_LABEL: Record<string, string> = {
  START_DATE: "On the loom since",
  SAREE_NUMBER: "Saree number",
  LOOM_CODE: "Loom number",
  RETIRED: "Retired",
  SERIES_START: "Saree number series",
};

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Old and new value as shown: dates as "19 Oct 2026", yes/no as is, blanks as "—". */
export const changeValues = (c: ChangeRow, fmtDay: (iso: string) => string): { from: string; to: string } => {
  const show = (v?: string | null) => (!v ? "—" : ISO_DAY.test(v) ? fmtDay(v) : v);
  return { from: show(c.oldValue), to: show(c.newValue) };
};

/** Looms put away by the owner never show in the rounds, daily work or attendance pickers. */
export const withoutRetired = <T extends { looms?: any[] } | null | undefined>(data: T): T =>
  data && Array.isArray(data.looms) ? ({ ...data, looms: data.looms.filter((l) => !l?.retired) } as T) : data;

/** Digits only, as the server wants a saree number. */
export const cleanSareeNumber = (raw: string) => raw.replace(/[^\d]/g, "").slice(0, 9);

/** Loom code as the server stores it: upper case, no spaces. */
export const cleanLoomCode = (raw: string) => raw.toUpperCase().replace(/\s+/g, "").slice(0, 20);

export type PersonRef = { id: string; code: string; name?: string };

/**
 * Who the loom entry starts from, so the screen can ask "same as last time?". Never saved silently:
 * the entry waits until this is confirmed or changed. `fromLast` is false when nobody has entered
 * work on this saree yet and the loom's usual people are shown instead.
 */
export const peopleToConfirm = (
  loom: any
): { weaver: PersonRef | null; helper: PersonRef | null; helperMode: string; fromLast: boolean; helperChanged: boolean } => {
  const last = loom?.lastPeople || null;
  const a = loom?.assignment || null;
  const helperMode = (last ? last.helperMode : a?.helperMode) || "WITH_HELPER";
  return {
    weaver: (last?.weaver || a?.weaver || null) as PersonRef | null,
    helper: helperMode === "WITH_HELPER" ? ((last ? last.helper : a?.helper) || null) : null,
    helperMode,
    fromLast: !!last,
    helperChanged: !!loom?.helperChangedRecently,
  };
};
